'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, cloneElement, forwardRef, isValidElement, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
const STATUS_LABELS = {
    online: 'Online',
    away: 'Away',
    busy: 'Busy',
    offline: 'Offline',
};
export function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
    return (first + last).toUpperCase();
}
/** Stable hash per seed so the same person gets the same fallback colour everywhere. */
function hashOf(seed, modulo) {
    let h = 0;
    for (let i = 0; i < seed.length; i++)
        h = (h * 31 + seed.charCodeAt(i)) % modulo;
    return h;
}
/** Index (1–5) of the `--mn-color-data-*` token a seed maps to with `palette="tokens"`. */
export function avatarPaletteIndex(seed) {
    return (hashOf(seed, 360) % 5) + 1;
}
function PersonGlyph() {
    return (_jsxs("svg", { "aria-hidden": true, focusable: false, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "8", cy: "5.5", r: "2.75" }), _jsx("path", { d: "M2.75 14c.6-2.6 2.7-4.25 5.25-4.25s4.65 1.65 5.25 4.25" })] }));
}
/**
 * A person or entity image with a fallback chain: image, then initials on a
 * colour stable per seed, then an icon.
 */
export const Avatar = forwardRef(function Avatar({ name, src, size = 'md', shape = 'circle', decorative = false, seed, palette = 'hue', fallback = 'initials', icon, status, statusLabel, className, style, ...rest }, ref) {
    const [failedSrc, setFailedSrc] = useState(null);
    const showImage = Boolean(src) && failedSrc !== src;
    const text = initials(name);
    const showIcon = !showImage && (fallback === 'icon' || !/\p{L}|\p{N}/u.test(text));
    const key = seed ?? name;
    const statusText = status ? (statusLabel ?? STATUS_LABELS[status]) : undefined;
    const a11y = decorative
        ? { 'aria-hidden': true }
        : {
            role: 'img',
            'aria-label': statusText ? `${name} (${statusText})` : name,
        };
    const numeric = typeof size === 'number';
    const vars = palette === 'tokens' ? {} : { ['--mn-avatar-hue']: String(hashOf(key, 360)) };
    if (numeric)
        vars['--mn-avatar-size'] = `${size}px`;
    return (_jsxs("span", { ref: ref, ...rest, ...a11y, className: cx('mn-avatar', className), "data-size": numeric ? 'custom' : size, "data-shape": shape, "data-palette": palette === 'tokens' ? avatarPaletteIndex(key) : undefined, "data-status": status, style: { ...style, ...vars }, children: [showImage ? (_jsx("img", { className: "mn-avatar__image", src: src ?? undefined, alt: "", onError: () => setFailedSrc(src ?? null) })) : showIcon ? (_jsx("span", { className: "mn-avatar__icon", "aria-hidden": true, children: icon ?? _jsx(PersonGlyph, {}) })) : (_jsx("span", { className: "mn-avatar__initials", "aria-hidden": true, children: text })), status ? _jsx("span", { className: "mn-avatar__status", "aria-hidden": true }) : null] }));
});
/**
 * Overlapping stack of avatars with a "+N" overflow tile. Each avatar keeps
 * its own name, so the group reads as a list of people.
 */
export const AvatarGroup = forwardRef(function AvatarGroup({ label, max, size = 'md', shape, overflowLabel = (n) => `${n} more`, className, style, children, ...rest }, ref) {
    const avatars = Children.toArray(children).filter(isValidElement);
    const limit = max === undefined ? avatars.length : Math.max(0, Math.floor(max));
    // A "+1" tile would take the same space as the avatar it hides, so show that avatar instead.
    const visibleCount = avatars.length - limit === 1 ? avatars.length : limit;
    const visible = avatars.slice(0, visibleCount);
    const hidden = avatars.length - visible.length;
    const numeric = typeof size === 'number';
    return (_jsxs("div", { ref: ref, role: "group", "aria-label": label, ...rest, className: cx('mn-avatar-group', className), "data-size": numeric ? 'custom' : size, style: numeric ? { ...style, ['--mn-avatar-size']: `${size}px` } : style, children: [visible.map((child) => cloneElement(child, {
                size: child.props.size ?? size,
                ...(shape && !child.props.shape ? { shape } : {}),
            })), hidden > 0 ? (_jsx("span", { role: "img", "aria-label": overflowLabel(hidden), className: "mn-avatar mn-avatar-group__overflow", "data-size": numeric ? 'custom' : size, "data-shape": shape ?? 'circle', children: _jsx("span", { "aria-hidden": true, children: `+${hidden}` }) })) : null] }));
});
//# sourceMappingURL=avatar.js.map